import React from "react";
import CustomModal from "../../../components/Modal/CustomModal";
import CustomTooltip from "../../../components/CustomTooltip";
import CustomChip from "../../../components/CustomChip";
import { PersonChip } from "../../../components/buttons/IconButtons";
import { EMPTY_VALUES } from "../../../Apis/functions";
import { RELATIONSHIP_TYPE_CHIP, RESIDENT_STATUS_CHIP, RESIDENT_TYPE, RESIDENT_TYPE_CHIP } from "../Model/Model";
import CustomGenericTittleIconDataChip from "../../../components/CustomGenericTittleIconDataChip";
import {colors} from "../../../enums/colors";
import ResidentAssignedCard from "../SubComponents/Residents/ResidentAssignedCard";
import NotInformation from "../../../components/NotInformation";
import { ResidentDetailsModalApp } from "../Functionality/ResidentDetailsModalApp";

/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 *
 * Read-only details of one registered resident of the building: the name with
 * its verification status chip on the header, the contact information and the
 * rest of the residents of their apartment, read from the apartment endpoint.
 *
 * @param {boolean} props.show - Modal visibility.
 * @param {Function} props.onHide - Closes the modal.
 * @param {Object} props.item - Resident shown in the modal.
 */
const ResidentDetailsModal = ({
    show,
    onHide,
    item
}) => {
    const {
        t,
        household,
        loading
    } = ResidentDetailsModalApp({ show, item });
    const statusChip = RESIDENT_STATUS_CHIP[item?.status];
    // Resident type of the card that opened the modal, painted next to the status
    const typeChip = RESIDENT_TYPE_CHIP[item?.residentType];
    // The owner of the apartment is only named for the residents that are not the owner
    const showOwner = item?.residentType !== RESIDENT_TYPE.OWNER;
    // Owner of the apartment, the card paints their email and no relationship,
    // the owner is the one the rest of the residents are related to
    const owner = household?.owner && { ...household.owner, relationship: '' };
    // Relationship of the resident shown with the owner of the apartment, the
    // RELATIONSHIP_TYPE values are translated and any other one is free text
    const relationship = item?.relationshipType || item?.relationship;
    const relationshipTrans = RELATIONSHIP_TYPE_CHIP[relationship]?.translation;

    return (
        <CustomModal
            show={show}
            onHide={onHide}
            size={'lg'}
            confirmButton={false}
            cancelButton={false}
            buttonCancelText={'dismiss'}
            topLeftHeader={
                <div className='d-flex align-items-center pb-3'>
                    <div><PersonChip /></div>
                    <p className='fs-24px ibl-font-w-600 font-color-general-950 font-color-primary-600 ml-2'>
                        {item?.fullName || EMPTY_VALUES.DEFAULT}
                    </p>
                    <div className='vertical-separator-shades-300' />
                    { statusChip &&
                        <div className={"flex-shrink-0"}>
                            <CustomChip
                                background={statusChip.background}
                                fontColor={statusChip.fontColor}
                                borderColor={statusChip.borderColor}
                                legend={t(statusChip.translation)}
                                variant={statusChip.variant || 'large'}
                                icon={statusChip.icon}
                                classNameIcon={statusChip.classNameIcon}
                            />
                        </div>
                    }
                    { typeChip &&
                        <div className={"flex-shrink-0 ml-2"}>
                            <CustomChip
                                background={typeChip.background}
                                fontColor={typeChip.fontColor}
                                borderColor={typeChip.borderColor}
                                legend={t(typeChip.translation)}
                                variant={typeChip.variant || 'large'}
                                icon={typeChip.icon}
                                classNameIcon={typeChip.classNameIcon}
                            />
                        </div>
                    }
                </div>
            }
        >
            <div className={"row border-full-shades-300 border-radius-8px p-16px"}>

                <div className={"col-12 col-md-6 mt-2 mb-3"}>
                    <CustomGenericTittleIconDataChip
                        title={'email'}
                        showAvatar={false}
                        className={'px-0'}
                    />
                    <CustomTooltip
                        text={`${item?.email || EMPTY_VALUES.DEFAULT}`}
                        classes={"fs-14px font-color-general-950 text-ellipsis-1-row p-0"}
                        showIfTruncated={true}
                        tooltipClasses={"p-0"}
                    />
                </div>

                <div className={"col-12 col-md-6 mt-2 mb-3"}>
                    <CustomGenericTittleIconDataChip
                        title={'apartment'}
                        showAvatar={false}
                        className={'px-0'}
                    />
                    <CustomTooltip
                        text={`${item?.apartment || household?.apartment || EMPTY_VALUES.DEFAULT}`}
                        classes={"fs-14px font-color-general-950 text-ellipsis-1-row p-0"}
                        showIfTruncated={true}
                        tooltipClasses={"p-0"}
                    />
                </div>

                { showOwner && owner && <>
                    <div className={"col-12 col-md-6 mt-2 mb-3"}>
                        <CustomGenericTittleIconDataChip
                            title={'main_resident'}
                            showAvatar={false}
                            className={'px-0'}
                        />
                        <div className={"mt-2"}>
                            <ResidentAssignedCard
                                keyId={'id'}
                                item={owner}
                            />
                        </div>
                    </div>
                </>}

                { showOwner && <>
                    <div className={"col-12 col-md-6 mt-2 mb-3"}>
                        <CustomGenericTittleIconDataChip
                            title={'relationship'}
                            showAvatar={false}
                            className={'px-0'}
                        />
                        <CustomChip
                            background={colors.secondary100}
                            borderColor={colors.secondary150}
                            fontColor={colors.secondary700}
                            legend={`${relationshipTrans ? t(relationshipTrans) : (relationship || EMPTY_VALUES.DEFAULT)}`}
                            variant='compact'
                            className={"mt-2"}
                        />
                    </div>
                </>}

                <div className={"row mt-3"}>
                    <CustomGenericTittleIconDataChip
                        title={'household_members'}
                        showAvatar={false}
                        className={'px-0 mb-3'}
                        badge={
                            <CustomChip
                                background={colors.shades200}
                                fontColor={colors.shades500}
                                borderColor={colors.shades300}
                                legend={household?.members?.length ?? 0}
                                variant='compact'
                            />
                        }

                    />
                    { household?.members?.length > 0 ?
                        household.members.map((member) => (
                            <div className={"col-12 col-md-6 mb-3 px-1"} key={member?.id}>
                                <ResidentAssignedCard
                                    keyId={'id'}
                                    item={member}
                                />
                            </div>
                        ))
                        : !loading && <NotInformation text={t('no_additional_household_members')} card={true}/>
                    }
                </div>
            </div>
        </CustomModal>
    );
};

export default ResidentDetailsModal;
